import React, { useState, useEffect } from 'react';
import rateLimiter from '../services/rateLimiter';

const RatingWidget = ({ onRate }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  // Проверяем, голосовал ли пользователь ранее
  useEffect(() => {
    const clientIP = rateLimiter.getClientID();
    const ratedBefore = localStorage.getItem(`rated_${clientIP}`);
    if (ratedBefore) {
      setHasRated(true);
      setRating(parseInt(ratedBefore));
    }
  }, []);

  const handleRatingClick = (value) => {
    if (hasRated) return;
    setRating(value);
    setShowFeedback(true);
  };

  const handleSubmit = async () => {
    if (hasRated || !rating) return;
    
    setIsSubmitting(true);
    const clientIP = rateLimiter.getClientID();
    
    try {
      // Отправляем рейтинг в Firebase
      await onRate(rating, feedback, clientIP);
      
      // Сохраняем в localStorage, что пользователь уже проголосовал
      localStorage.setItem(`rated_${clientIP}`, rating.toString());
      setHasRated(true);
      
      // Показываем благодарность
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
    } catch (error) {
      console.error('Ошибка отправки рейтинга:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingText = (value) => {
    const texts = {
      1: 'Плохо',
      2: 'Неудовлетворительно',
      3: 'Нормально',
      4: 'Хорошо',
      5: 'Отлично'
    };
    return texts[value] || '';
  };

  const getRatingEmoji = (value) => {
    const emojis = {
      1: '😞',
      2: '😕',
      3: '😐',
      4: '😊',
      5: '🤩'
    };
    return emojis[value] || '';
  };

  if (hasRated && !showFeedback) {
    return (
      <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg animate-fade-in-up">
        <div className="flex items-center justify-center gap-3">
          <div className="text-3xl">{getRatingEmoji(rating)}</div>
          <div>
            <h4 className="text-sm font-semibold text-green-800">
              Спасибо за вашу оценку!
            </h4>
            <p className="text-xs text-green-600">
              Вы оценили наш сервис: {getRatingText(rating)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 p-4 md:p-6 bg-white border-2 border-gray-200 rounded-lg animate-fade-in-up">
      <div className="text-center mb-4">
        <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1">
          Оцените наш сервис
        </h3>
        <p className="text-xs md:text-sm text-gray-600">
          Ваше мнение поможет нам стать лучше
        </p>
      </div>

      {/* Звёзды рейтинга */}
      <div className="flex justify-center gap-2 md:gap-3 mb-4">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => handleRatingClick(value)}
            onMouseEnter={() => !hasRated && setHoveredRating(value)}
            onMouseLeave={() => !hasRated && setHoveredRating(0)}
            disabled={hasRated}
            className={`
              transition-all duration-200 transform
              ${hasRated ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
              ${(hoveredRating >= value || rating >= value) ? 'scale-110' : 'scale-100'}
            `}
          >
            <svg
              className={`w-8 h-8 md:w-10 md:h-10 transition-colors duration-200
                ${(hoveredRating >= value || rating >= value)
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300 fill-current'
                }
              `}
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </button>
        ))}
      </div>

      {/* Текст рейтинга */}
      {(hoveredRating > 0 || rating > 0) && !hasRated && (
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-2 text-lg font-medium text-gray-800 animate-fade-in">
            <span className="text-2xl">{getRatingEmoji(hoveredRating || rating)}</span>
            {getRatingText(hoveredRating || rating)}
          </span>
        </div>
      )}

      {/* Форма отзыва */}
      {showFeedback && rating > 0 && !hasRated && (
        <div className="animate-slide-up">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Расскажите подробнее (необязательно)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={3}
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-gray-500">
              {feedback.length}/500
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowFeedback(false);
                  setRating(0);
                  setFeedback('');
                }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подсказка */}
      {!showFeedback && rating === 0 && !hasRated && (
        <p className="text-xs text-center text-gray-500 mt-2">
          Нажмите на звёздочку, чтобы оценить
        </p>
      )}
    </div>
  );
};

export default RatingWidget;

















